// ============================================
// AnonymousChatWindow Component
// Chat window for non-authenticated users - matches ChatWindow aesthetics
// ============================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useChatContext } from '@/context/ChatContext';
import { useChatMessages } from '@/hooks/useChat';
import { useAnonymousGroupChat } from '@/hooks/useAnonymousGroupChat';
import { useSession } from '@/context/SessionContext';
import { sendAnonymousUserGroupMessage } from '@/lib/supabase/chat';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  X,
  ChevronDown,
  Maximize2,
  Send,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAvatarInitials } from '@/lib/utils';
import { format } from 'date-fns';
import type { ChatWindowState } from '@/types/chat';
import { supabase } from '@/lib/supabase/client';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface AnonymousChatWindowProps {
  window: ChatWindowState;
  index: number;
}

export const AnonymousChatWindow: React.FC<AnonymousChatWindowProps> = ({ window, index }) => {
  const { closeChatWindow, minimizeChatWindow, maximizeChatWindow } = useChatContext();
  const { session } = useSession();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [resolvedStoreAvatarUrl, setResolvedStoreAvatarUrl] = useState<string | null>(window.avatarUrl || null);
  const isInitialLoad = useRef(true);
  const hasUserScrolled = useRef(false);
  const previousMessageCountRef = useRef(0);

  const isTouchInputDevice = () => {
    if (typeof window === 'undefined') return false;
    const hasTouchPoints = navigator.maxTouchPoints > 0;
    const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
    return Boolean(hasTouchPoints || coarsePointer);
  };
  
  const effectiveAvatarUrl = resolvedStoreAvatarUrl || window.avatarUrl || null;

  // Get or create group chat for this anonymous session
  const { groupChatId, isCreating: isCreatingGroupChat, error: groupChatError } = useAnonymousGroupChat({
    anonymousSessionId: window.anonymousSessionId,
    storeId: window.anonymousStoreId,
  });

  // Use standard group chat messages hook
  // Pass anonymousSessionId so it handles anon users properly
  // But also pass groupId for the new group chat infrastructure
  const { messages, readByMessageId, loading, sendMessage } = useChatMessages({
    groupId: groupChatId || undefined,
    anonymousSessionId: window.anonymousSessionId,
  });

  useEffect(() => {
    let cancelled = false;

    const loadStoreAvatar = async () => {
      if (!window.anonymousStoreId) {
        setResolvedStoreAvatarUrl(window.avatarUrl || null);
        return;
      }

      const { data, error } = await supabase
        .from('stores')
        .select('avatar_url')
        .eq('id', window.anonymousStoreId)
        .maybeSingle();

      if (cancelled) return;

      if (!error && data?.avatar_url) {
        setResolvedStoreAvatarUrl(data.avatar_url);
        return;
      }

      setResolvedStoreAvatarUrl(window.avatarUrl || null);
    };

    void loadStoreAvatar();

    return () => {
      cancelled = true;
    };
  }, [window.anonymousStoreId, window.avatarUrl]);

  useEffect(() => {
    if (!window.anonymousSessionId) {
      console.error('AnonymousChatWindow: anonymousSessionId is missing!', window);
    }
  }, [window.anonymousSessionId]);

  const getScrollViewport = useCallback(() => {
    return scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
  }, []);

  const isNearBottom = useCallback(() => {
    const viewport = getScrollViewport();
    if (!viewport) return true;
    const threshold = 88;
    const distanceToBottom = viewport.scrollHeight - (viewport.scrollTop + viewport.clientHeight);
    return distanceToBottom <= threshold;
  }, [getScrollViewport]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => {
      const viewport = getScrollViewport();
      if (!viewport) return;
      viewport.scrollTo({ top: viewport.scrollHeight, behavior });
    });
  }, [getScrollViewport]);

  // Reset scroll state when switching chat window.
  useEffect(() => {
    isInitialLoad.current = true;
    hasUserScrolled.current = false;
    previousMessageCountRef.current = 0;
    setShowJumpToLatest(false);
    setNewMessagesCount(0);
  }, [window.id]);

  // Initial load: jump to bottom once without smooth animation.
  useEffect(() => {
    if (!messages.length || !isInitialLoad.current) return;
    scrollToBottom('auto');
    previousMessageCountRef.current = messages.length;
    isInitialLoad.current = false;
  }, [messages.length, scrollToBottom]);

  // Smart autoscroll behavior for incoming messages.
  useEffect(() => {
    if (isInitialLoad.current || messages.length === 0) return;

    const previousCount = previousMessageCountRef.current;
    const hasNewMessages = messages.length > previousCount;
    const incomingCount = Math.max(0, messages.length - previousCount);
    previousMessageCountRef.current = messages.length;

    if (!hasNewMessages) return;

    const lastMessage = messages[messages.length - 1] as any;
    const isOwnMessage = lastMessage?.metadata?.is_anonymous === true;

    if (isOwnMessage || isNearBottom()) {
      setShowJumpToLatest(false);
      setNewMessagesCount(0);
      scrollToBottom('smooth');
      return;
    }

    setNewMessagesCount((prev) => prev + incomingCount);
    setShowJumpToLatest(true);
  }, [messages, isNearBottom, scrollToBottom]);

  // Track manual upward scrolling to preserve user reading position.
  useEffect(() => {
    const viewport = getScrollViewport();
    if (!viewport) return;

    let lastScrollTop = viewport.scrollTop;

    const handleScroll = () => {
      if (!isInitialLoad.current && viewport.scrollTop < lastScrollTop) {
        hasUserScrolled.current = true;
      }

      if (isNearBottom()) {
        setShowJumpToLatest(false);
        setNewMessagesCount(0);
      }

      lastScrollTop = viewport.scrollTop;
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [getScrollViewport, isNearBottom]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendError(null);
    if (!messageInput.trim() || sending) return;

    if (!window.anonymousSessionId) {
      setSendError('No se encontró la sesión anónima. Refresca la página.');
      return;
    }

    if (!groupChatId) {
      setSendError('Cargando la conversación...');
      return;
    }

    try {
      setSending(true);
      // Send as anonymous user to group chat
      await sendAnonymousUserGroupMessage(groupChatId, window.anonymousSessionId, messageInput.trim());
      setMessageInput('');
      if (textInputRef.current) {
        textInputRef.current.style.height = 'auto';
      }
      setShowJumpToLatest(false);
      setNewMessagesCount(0);
      scrollToBottom('smooth');
    } catch (error: any) {
      setSendError(error?.message || 'Error al enviar el mensaje');
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleMinimize = () => {
    setIsMinimizing(true);
    setTimeout(() => {
      minimizeChatWindow(window.id);
      setIsMinimizing(false);
    }, 150);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      closeChatWindow(window.id);
      setIsClosing(false);
    }, 150);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);
    if (textInputRef.current) {
      textInputRef.current.style.height = 'auto';
      textInputRef.current.style.height = Math.min(textInputRef.current.scrollHeight, 120) + 'px';
    }
  };

  // Position calculations
  const isMobile = typeof globalThis.window !== 'undefined' && globalThis.window.innerWidth < 1024;
  const [mobileNavbarHeight, setMobileNavbarHeight] = useState<number>(0);

  useEffect(() => {
    const updateNavbarHeight = () => {
      try {
        const el = document.querySelector('.app-bottom-nav') as HTMLElement | null;
        const h = el ? el.getBoundingClientRect().height : 0;
        setMobileNavbarHeight(h);
      } catch {
        setMobileNavbarHeight(0);
      }
    };
    updateNavbarHeight();
    if (typeof globalThis !== 'undefined' && (globalThis as any).addEventListener) {
      (globalThis as any).addEventListener('resize', updateNavbarHeight);
      return () => (globalThis as any).removeEventListener('resize', updateNavbarHeight);
    }
  }, []);

  const computedZIndex = 10005 - index;
  const minimizedZIndex = computedZIndex;
  const rightOffset = !isMobile ? 16 + (index * 320) : undefined;
  const bottomOffset = 16;

  // Minimized state
  if (window.isMinimized) {
    return (
      <div
        className={cn(
          'fixed bottom-0 w-80 bg-background border rounded-t-lg shadow-lg max-md:fixed max-md:w-full max-md:left-0 max-md:right-0 max-md:rounded-none',
          'transition-all duration-200',
          isClosing && 'animate-out slide-out-to-bottom duration-200'
        )}
        style={{
          right: isMobile ? undefined : `${rightOffset}px`,
          bottom: `${bottomOffset + (isMobile ? mobileNavbarHeight : 0)}px`,
          zIndex: minimizedZIndex,
        }}
        data-chat-window
      >
        <div
          role="button"
          onClick={() => maximizeChatWindow(window.id)}
          className="w-full p-3 flex items-center justify-between transition-colors rounded-t-lg max-md:rounded-none hover:bg-primary/80 cursor-pointer bg-primary text-white"
        >
          <div className="flex items-center gap-3">
            <Avatar className="w-8 h-8 rounded">
              <AvatarImage src={effectiveAvatarUrl || undefined} />
              <AvatarFallback>{getAvatarInitials(window.title || '')}</AvatarFallback>
            </Avatar>
            <span className="font-bold !text-white">{window.title}</span>
          </div>
          <button
            type="button"
            className="p-1 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    );
  }

  // Maximized state
  return (
    <div
      className={cn(
        'fixed flex flex-col bg-background shadow-xl border-t overflow-hidden',
        'w-80 h-[500px] md:h-[500px] rounded-t-lg border-x',
        'max-md:fixed max-md:right-0 max-md:w-full max-md:rounded-none max-md:border-none',
        isClosing || isMinimizing
          ? 'animate-out slide-out-to-bottom duration-200'
          : 'animate-in slide-in-from-bottom duration-200'
      )}
      style={{
        right: isMobile ? 0 : `${rightOffset}px`,
        bottom: 0,
        top: isMobile ? 0 : 'auto',
        height: isMobile ? '100dvh' : '500px',
        zIndex: computedZIndex,
        backgroundColor: 'hsl(var(--background))',
      }}
      data-chat-window
    >
      {/* Safe Area Spacer for Mobile */}
      {isMobile && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

      {/* Header - Matches ChatWindow design */}
      <div className="flex items-center justify-between p-3 border-b bg-primary text-white min-h-[64px] shrink-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Avatar className="w-8 h-8 rounded">
            <AvatarImage src={effectiveAvatarUrl || undefined} />
            <AvatarFallback>{getAvatarInitials(window.title || '')}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-bold truncate !text-white select-none">{window.title}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <div
            role="button"
            tabIndex={-1}
            className="h-8 w-8 hidden md:flex text-white hover:bg-white/10 items-center justify-center rounded cursor-pointer transition-colors"
            onClick={() => {
              // Maximize on desktop (could open full chat page if needed)
            }}
          >
            <Maximize2 className="w-4 h-4" />
          </div>
          <div
            role="button"
            tabIndex={-1}
            className="h-8 w-8 text-white hover:bg-white/10 flex items-center justify-center rounded cursor-pointer transition-colors"
            onClick={handleMinimize}
          >
            <ChevronDown className="w-4 h-4" />
          </div>
          <button
            type="button"
            tabIndex={-1}
            className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Messages - Matches ChatWindow layout */}
      <ScrollArea className="flex-1 px-4" ref={scrollAreaRef}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Cargando mensajes...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground text-center">
              No hay mensajes aún.
              <br />
              <span className="text-sm">¡Envía el primero!</span>
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-4">
            {messages.map((message, idx) => {
              // Para el usuario anónimo:
              // - Mensajes del usuario anónimo (metadata.is_anonymous = true) → RIGHT (yo)
              // - Mensajes del staff (no tienen esa flag) → LEFT (soporte)
              const isMyMessage = message.metadata?.is_anonymous === true;
              const isSystemMessage = message.metadata?.is_system === true;
              const seenByUsers = (readByMessageId[message.id] || [])
                .filter((reader) => reader.userId !== message.sender_id)
                .slice(0, 5);
              
              const showTimestamp =
                idx === 0 ||
                new Date(message.created_at).getTime() -
                  new Date(messages[idx - 1].created_at).getTime() >
                  300000; // 5 minutes

              return (
                <div
                  key={message.id}
                  className={cn(
                    'flex flex-col',
                    isMyMessage ? 'items-end' : 'items-start',
                    idx >= messages.length - 2 && 'animate-in fade-in-0 slide-in-from-bottom-1 duration-200'
                  )}
                >
                  {showTimestamp && (
                    <div className="w-full text-center text-xs text-muted-foreground my-2">
                      {format(new Date(message.created_at), 'dd/MM/yyyy HH:mm')}
                    </div>
                  )}

                  {isSystemMessage ? (
                    <div className="flex justify-center w-full">
                      <div className="bg-muted/50 text-muted-foreground text-xs px-3 py-2 rounded-full">
                        {message.content || message.message}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'flex gap-2 max-w-[70%] min-w-0',
                        isMyMessage ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <div
                        className={cn(
                          'rounded-lg px-3 py-2 break-words border',
                          isMyMessage
                            ? 'bg-primary/15 text-foreground border-primary/35 rounded-tr-sm'
                            : 'bg-muted border-muted'
                        )}
                      >
                        <p className="text-sm whitespace-pre-wrap">
                          {message.content || message.message}
                        </p>
                      </div>
                    </div>
                  )}
                  {isMyMessage && seenByUsers.length > 0 && (
                    <div className="mt-1 flex w-full items-center justify-end gap-1 pr-1">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Visto</span>
                      <div className="flex flex-row-reverse items-center">
                        <TooltipProvider>
                          {seenByUsers.map((reader) => {
                            const readerName = reader.displayName || reader.username || 'Usuario';
                            return (
                              <Tooltip key={`${message.id}-${reader.userId}`}>
                                <TooltipTrigger asChild>
                                  <Avatar className="w-4 h-4 border border-background -mr-1 first:mr-0 bg-background">
                                    <AvatarImage src={reader.avatarUrl || undefined} />
                                    <AvatarFallback className="text-[8px]">
                                      {getAvatarInitials(readerName)}
                                    </AvatarFallback>
                                  </Avatar>
                                </TooltipTrigger>
                                <TooltipContent side="top">{readerName}</TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </TooltipProvider>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {showJumpToLatest && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20">
          <button
            type="button"
            onClick={() => {
              setShowJumpToLatest(false);
              setNewMessagesCount(0);
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary text-primary-foreground shadow-md hover:opacity-95 transition-opacity"
          >
            {newMessagesCount > 1 ? `Ver ${newMessagesCount} mensajes nuevos` : 'Ver mensaje nuevo'}
          </button>
        </div>
      )}

      {/* Input - Matches ChatWindow input style */}
      <form
        onSubmit={handleSendMessage}
        className="flex gap-2 p-3 border-t bg-background shrink-0"
      >
        <textarea
          ref={textInputRef}
          value={messageInput}
          onChange={handleTextareaChange}
          onKeyDown={(e) => {
            if (isTouchInputDevice()) {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSendMessage(e as any);
              }
              return;
            }

            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e as any);
            }
          }}
          placeholder="Escribe un mensaje..."
          disabled={sending || loading}
          className="flex-1 resize-none border rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[40px] max-h-[120px]"
          rows={1}
        />
        <Button
          type="submit"
          disabled={sending || !messageInput.trim() || !window.anonymousSessionId}
          size="icon"
          className="h-10 w-10 shrink-0"
          aria-label="Enviar mensaje"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </form>

      {/* Error feedback */}
      {sendError && (
        <div className="px-3 py-2 text-center bg-destructive/10 border-t text-xs text-destructive">
          {sendError}
        </div>
      )}
      {/* Info badge - Anonymous user */}
      <div className="px-3 py-2 text-center bg-muted/50 border-t text-xs text-muted-foreground">
        {session?.user?.id
          ? 'Chat temporal activo • Se migrara a tu cuenta automaticamente'
          : 'Conectado como visitante • Inicia sesion para guardar el chat'}
      </div>
    </div>
  );
};

export default AnonymousChatWindow;
